import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SharedTextInput, SharedButton } from "../../../shared";
import { useCreateMarketplaceItemMutation } from "../../../../store/slices/api";
import { selectUser } from "../../../../store/slices/authentication";
import { useSelector } from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import ImageUploader from "./ImageUploader";
import FileUploader from "../../Bulletin/CreatePost/FileUploader";
import {
  HANDLE_LABELS,
  PAYMENT_PLATFORMS,
  handlePreview,
  isLinkablePlatform,
  normalizeHandle,
  paymentHandleError,
  sellerEmailError,
} from "../payLinks";
import { CATEGORIES } from "../Items/itemFormat";
import { serverReason } from "../serverReason";

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 2000;
const MAX_EMAIL_LENGTH = 100;
// Room for a pasted link (e.g. https://account.venmo.com/u/your-name); it is
// reduced to the bare name before saving.
const MAX_HANDLE_LENGTH = 100;

const POST_FAILED =
  "Your listing couldn't be posted. Nothing was lost — please check the details and try again.";

function CreateItem() {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [createMarketplaceItem, { isLoading }] =
    useCreateMarketplaceItemMutation();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState(null);
  const [categoryOther, setCategoryOther] = useState("");
  const [price, setPrice] = useState("");
  const [priceUnit, setPriceUnit] = useState("");
  const [imageUrls, setImageUrls] = useState([]);
  const [sellerEmail, setSellerEmail] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [paymentPlatform, setPaymentPlatform] = useState(null);
  const [paymentHandle, setPaymentHandle] = useState("");
  const [submitError, setSubmitError] = useState(null);
  // Photos or files still uploading: Post waits, or they'd be left off.
  const [photosUploading, setPhotosUploading] = useState(false);
  const [filesUploading, setFilesUploading] = useState(false);
  const uploading = photosUploading || filesUploading;

  // Checked as they type, with the same rules the server uses, so a bad
  // handle is caught here rather than after posting.
  const handleError = paymentPlatform
    ? paymentHandleError(paymentPlatform.value, paymentHandle)
    : null;

  const emailError = sellerEmailError(sellerEmail);

  const shouldDisableCreate = () => {
    return (
      Boolean(handleError) ||
      Boolean(emailError) ||
      title.length === 0 ||
      description.length === 0 ||
      !category ||
      sellerEmail.trim().length === 0 ||
      uploading ||
      isLoading
    );
  };

  // For Venmo / Cash App / PayPal a pasted link or "@name" is saved as the
  // bare name -- never the raw text. (Posting is blocked while it can't be.)
  const savedHandle = () => {
    const trimmed = paymentHandle.trim();
    if (!paymentPlatform || !trimmed) return null;
    if (isLinkablePlatform(paymentPlatform.value)) {
      return normalizeHandle(paymentPlatform.value, trimmed);
    }
    return trimmed;
  };

  const handleSubmit = async () => {
    if (isLoading || uploading || handleError || emailError) return;
    setSubmitError(null);
    const itemData = {
      individual_id: parseInt(user.externalId),
      title,
      description,
      category,
      category_other: category === "other" ? categoryOther : null,
      price: price ? parseFloat(price) : null,
      price_unit: priceUnit || null,
      image_urls: imageUrls,
      attachments,
      seller_email: sellerEmail.trim(),
      payment_platform: paymentPlatform ? paymentPlatform.value : null,
      payment_handle: savedHandle(),
    };

    // Only leave the form once the listing is really saved. On failure every
    // field and upload stays as it was, and the member is told why.
    try {
      await createMarketplaceItem(itemData).unwrap();
    } catch (err) {
      const reason = serverReason(err);
      setSubmitError(reason ? `${POST_FAILED} (${reason})` : POST_FAILED);
      return;
    }
    navigate("/marketplace?tab=items");
  };

  return (
    <div className="flex flex-col rounded-[16px] shadow-xl dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-[32px] border border-dupontGold bg-white dark:bg-darkCard gap-y-[16px]">
      <div className="text-[18px] font-bold text-dupontBlue dark:text-darkText">Post an Item</div>

      <SharedTextInput
        label="Title*"
        name="title"
        value={title}
        onChange={setTitle}
        variant="outlined"
        maxChars={MAX_TITLE_LENGTH}
      />

      <SharedTextInput
        label="Description*"
        name="description"
        value={description}
        onChange={setDescription}
        multiline
        rows={4}
        variant="outlined"
        maxChars={MAX_DESCRIPTION_LENGTH}
      />

      <Autocomplete
        options={CATEGORIES}
        value={category}
        onChange={(event, newValue) => setCategory(newValue)}
        renderInput={(params) => (
          <TextField {...params} label="Category*" variant="outlined" />
        )}
      />

      {category === "other" && (
        <SharedTextInput
          label="Describe your category"
          name="categoryOther"
          value={categoryOther}
          onChange={setCategoryOther}
          variant="outlined"
          maxChars={35}
        />
      )}

      <SharedTextInput
        label="Price ($) — leave blank for 'Make an offer'"
        name="price"
        value={price}
        onChange={(val) => {
          if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
            setPrice(val);
          }
        }}
        variant="outlined"
      />

      <SharedTextInput
        label="Price unit"
        name="priceUnit"
        value={priceUnit}
        onChange={setPriceUnit}
        variant="outlined"
        maxChars={25}
        placeholder="per week"
        helperText="For rentals or real estate, add a unit like per week / per month / per night. Leave blank for one-time sales."
      />

      <ImageUploader
        imageUrls={imageUrls}
        onImageUrlsChange={(val) =>
          typeof val === "function" ? setImageUrls(val) : setImageUrls(val)
        }
        onUploadingChange={setPhotosUploading}
      />

      <div className="text-[14px] font-bold text-dupontBlue dark:text-darkText mt-[8px]">
        File Attachments (optional)
      </div>
      <div className="text-[13px] text-dupontGray dark:text-darkTextSecondary">
        Attach documents, videos, or any other files — PDFs, Word docs, spreadsheets, presentations, images, videos, ZIPs, and more.
      </div>
      <FileUploader
        attachments={attachments}
        onAttachmentsChange={setAttachments}
        maxFiles={10}
        onUploadingChange={setFilesUploading}
      />

      <SharedTextInput
        label="Your Email (for buyers to contact you)*"
        name="sellerEmail"
        value={sellerEmail}
        onChange={setSellerEmail}
        variant="outlined"
        maxChars={MAX_EMAIL_LENGTH}
        error={emailError || ""}
      />

      <div className="text-[14px] font-bold text-dupontBlue dark:text-darkText mt-[8px]">
        Payment Info (optional)
      </div>
      <div className="text-[13px] text-dupontGray dark:text-darkTextSecondary">
        Share how buyers can pay you. For Venmo, Cash App and PayPal we make a
        safe "Pay with…" button from your username. No money ever passes
        through du Pont Circle — buyers pay you in their own app.
      </div>

      <Autocomplete
        options={PAYMENT_PLATFORMS}
        getOptionLabel={(option) => option.label}
        value={paymentPlatform}
        onChange={(event, newValue) => setPaymentPlatform(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Payment Platform"
            variant="outlined"
          />
        )}
      />

      {paymentPlatform && (
        <>
          <SharedTextInput
            label={HANDLE_LABELS[paymentPlatform.value] || `Your ${paymentPlatform.label} handle`}
            name="paymentHandle"
            value={paymentHandle}
            onChange={setPaymentHandle}
            variant="outlined"
            maxChars={MAX_HANDLE_LENGTH}
            error={handleError || ""}
          />
          {handlePreview(paymentPlatform.value, paymentHandle) && (
            <div className="text-[14px] font-body text-dupontGray dark:text-darkTextSecondary -mt-[8px]" aria-live="polite">
              {handlePreview(paymentPlatform.value, paymentHandle)}
            </div>
          )}
        </>
      )}

      {submitError && (
        <div role="alert" className="text-[15px] font-body text-red-700 dark:text-red-400">
          {submitError}
        </div>
      )}

      <SharedButton
        disabled={shouldDisableCreate()}
        onClick={handleSubmit}
        loadingText={isLoading ? "Posting item..." : uploading ? "Waiting for uploads..." : undefined}
      >
        Post Item
      </SharedButton>
    </div>
  );
}

export default CreateItem;
